import { useEffect } from "react";

/**
 * Keeps a block (a field together with its buttons) above the on-screen keyboard
 * while something inside it has focus. The keyboard opens a moment after the
 * field is tapped, so this follows the visible area as it shrinks instead of
 * guessing a delay. It also follows the block when the block itself grows.
 */
export function useRevealAboveKeyboard(ref, active = true) {
  useEffect(() => {
    if (!active) return undefined;
    const viewport = window.visualViewport;
    const reveal = () => {
      const block = ref.current;
      if (!block || !block.contains(document.activeElement)) return;
      const visibleBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      if (block.getBoundingClientRect().bottom + 12 <= visibleBottom) return;
      // scrollIntoView lines up with the full page area; the margin is the part the keyboard covers (iOS)
      block.style.scrollMarginBottom = `${Math.max(0, window.innerHeight - visibleBottom) + 12}px`;
      block.scrollIntoView({ block: "end" });
    };
    const target = viewport || window;
    target.addEventListener("resize", reveal);
    document.addEventListener("focusin", reveal);
    // The block can also grow while the keyboard is up (an error message appears):
    // what was above the keyboard a moment ago would slide under it.
    const grown = typeof ResizeObserver === "function" && ref.current ? new ResizeObserver(reveal) : null;
    grown?.observe(ref.current);
    return () => {
      target.removeEventListener("resize", reveal);
      document.removeEventListener("focusin", reveal);
      grown?.disconnect();
    };
  }, [ref, active]);
}
